import { useEffect, useState } from 'react';

interface UseAutocompleteNavigationProps {
  optionCount: number;
  isOpen: boolean;
  onSelect: (index: number) => void;
  onClose: () => void;
  defaultFocusedIndex?: number;
  suggestedFocusedIndex?: number;
}

function isInRange(optionCount: number, index?: number): index is number {
  return index !== undefined && index >= 0 && index < optionCount;
}

function seedFocusedIndex(optionCount: number, defaultFocusedIndex?: number): number {
  if (optionCount === 0) {
    return -1;
  }
  return isInRange(optionCount, defaultFocusedIndex) ? defaultFocusedIndex : 0;
}

export function useAutocompleteNavigation({
  optionCount,
  isOpen,
  onSelect,
  onClose,
  defaultFocusedIndex,
  suggestedFocusedIndex,
}: UseAutocompleteNavigationProps): {
  focusedIndex: number;
  enterFocusedIndex: number;
  containerDataProps: { 'data-autocomplete-active'?: string };
  handleKeyDown: (e: React.KeyboardEvent) => void;
} {
  const [rawFocusedIndex, setFocusedIndex] = useState(() => seedFocusedIndex(optionCount, defaultFocusedIndex));
  const [hasNavigated, setHasNavigated] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFocusedIndex(seedFocusedIndex(optionCount, defaultFocusedIndex));
      setHasNavigated(false);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only re-seed on open transition; defaultFocusedIndex / optionCount are read via closure
  }, [isOpen]);

  let focusedIndex = -1;
  if (isOpen && optionCount > 0) {
    if (hasNavigated && isInRange(optionCount, rawFocusedIndex)) {
      focusedIndex = rawFocusedIndex;
    } else if (isInRange(optionCount, suggestedFocusedIndex)) {
      focusedIndex = suggestedFocusedIndex;
    } else if (isInRange(optionCount, defaultFocusedIndex)) {
      focusedIndex = defaultFocusedIndex;
    } else if (isInRange(optionCount, rawFocusedIndex)) {
      focusedIndex = rawFocusedIndex;
    } else {
      focusedIndex = 0;
    }
  }
  const enterFocusedIndex = focusedIndex;

  const containerDataProps: { 'data-autocomplete-active'?: string } =
    focusedIndex >= 0 ? { 'data-autocomplete-active': 'true' } : {};

  function moveTo(index: number) {
    setHasNavigated(true);
    setFocusedIndex(index);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen) {
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }

    if (optionCount === 0) {
      return;
    }

    const acceptIndex = (e.key === 'Tab' && !e.shiftKey) || e.key === 'Enter' ? focusedIndex : -1;
    if (acceptIndex >= 0) {
      e.preventDefault();
      e.stopPropagation();
      onSelect(acceptIndex);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        moveTo(focusedIndex < 0 ? 0 : (focusedIndex + 1) % optionCount);
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        moveTo(focusedIndex <= 0 ? optionCount - 1 : focusedIndex - 1);
        break;
      default:
        break;
    }
  }

  return { focusedIndex, enterFocusedIndex, containerDataProps, handleKeyDown };
}
